// L2 (per-tenant studio) template data hooks. Backs the tenant_admin
// template authoring flow:
//   - useL2Templates(...)   — list (lifecycle-state-aware)
//   - useL2TemplateDetail(...) — single template by id
//   - useUploadL2Template() — multipart upload → DocAI → draft
//   - useSubmitL2Draft()    — submit fully-typed draft
//   - useCertifyL2Template() — flip draft → certified
//
// Distinct from `templatesQuery.ts` (which is L1-read-only for the
// attorney library) — these hooks span the full lifecycle and target
// the L2 routes mounted under /tenants/:tenantId/templates.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";
import type {
  CertifiedTemplateRow,
  SubmitDraftResponse,
  SubmitDraftVariables,
} from "./templatesQuery";

// Re-export the row type so callers don't need two imports. The wire
// shape is shared with L1 by design; only the URL prefix differs.
export type L2TemplateRow = CertifiedTemplateRow;

export type L2LifecycleFilter = "all" | "draft" | "certified" | "under_review" | "deprecated";

export type L2TemplatesListResponse = {
  templates: L2TemplateRow[];
};

export const L2_TEMPLATES_KEY = ["templates", "L2"] as const;

/**
 * Lists L2 templates for a tenant. Unlike useCertifiedTemplates (which
 * filters server-side to certified/re_certified only), this hook returns
 * EVERY lifecycle state — the authoring UI needs to show drafts and
 * under-review alongside certified.
 */
export function useL2Templates(
  tenantId: string,
  state: L2LifecycleFilter = "all"
): UseQueryResult<L2TemplateRow[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...L2_TEMPLATES_KEY, "list", tenantId, state] as const,
    enabled: tenantId.length > 0,
    queryFn: async () => {
      const res = await api<L2TemplatesListResponse>(
        `/tenants/${encodeURIComponent(tenantId)}/templates`
      );
      // Server returns all states; filter client-side.
      if (state === "all") return res.templates;
      return res.templates.filter((t) => t.lifecycleState === state);
    },
  });
}

export function useL2TemplateDetail(
  tenantId: string,
  templateId: string | undefined
): UseQueryResult<L2TemplateRow, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...L2_TEMPLATES_KEY, "detail", tenantId, templateId] as const,
    enabled:
      tenantId.length > 0 &&
      templateId !== undefined &&
      templateId.length > 0,
    queryFn: () =>
      api<L2TemplateRow>(
        `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId!)}`
      ),
  });
}

export type UploadL2Result = {
  /** Server-assigned template id (uuid or content-hash). */
  id: string;
  /** Extracted HTML body — caller edits this in CKEditor. DOCX goes through
   *  mammoth, not Document AI OCR (this comment used to say otherwise). */
  draftContent: string;
  /**
   * Non-fatal extraction notes from the server — inconsistent Word heading
   * styles that were distrusted and flattened, Word auto-numbering whose
   * section numbers can't survive the import, or a style mammoth couldn't map.
   * Empty for a clean import.
   *
   * NOT RENDERED YET — no upload screen displays these. The field exists so the
   * data stops being dropped silently at the type layer; wiring it into the
   * bulk-upload row and the single-template upload flow is the follow-up.
   */
  warnings?: string[];
};

export type UploadL2Input = {
  file: File;
  // S-216 — when true, request `?autoTag=1` so the response carries an
  // inline tag suggestion (or `tagsError` when tagging fails).
  autoTag?: boolean;
};

/**
 * Multipart-upload of a real DOCX/PDF. Server runs Document AI OCR and
 * returns the extracted text plus a server-assigned template id (no
 * lifecycle write yet — the caller decides to submit-draft + certify).
 */
export function useUploadL2Template(
  tenantId: string
): UseMutationResult<
  UploadL2Result & {
    tags?: import("./l1TemplatesQuery").TemplateTagSuggestion;
    tagsError?: string;
  },
  Error,
  UploadL2Input | File
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: async (input) => {
      const { file, autoTag } =
        input instanceof File ? { file: input, autoTag: false } : input;
      const form = new FormData();
      form.append("file", file);
      const query = autoTag === true ? "?autoTag=1" : "";
      return await api<
        UploadL2Result & {
          tags?: import("./l1TemplatesQuery").TemplateTagSuggestion;
          tagsError?: string;
        }
      >(
        `/tenants/${encodeURIComponent(tenantId)}/templates/upload${query}`,
        { method: "POST", body: form }
      );
    },
  });
}

export type SubmitL2DraftInput = {
  id: string;
  // S-216 — human-readable title (auto-tagged or manual).
  title?: string | null;
  dealType: L2TemplateRow["dealType"];
  guild: L2TemplateRow["guild"];
  tier: L2TemplateRow["tier"];
  content: string;
  effectiveFrom: string; // ISO datetime
  // S-216 — anchor CBA picked manually or via auto-tag.
  anchorCBA?: string | null;
  // [S-633] Agreement form — carried through so a draft save preserves it.
  documentForm?: L2TemplateRow["documentForm"];
  // [S-802 follow-up] Where the author's edit left each variable, so the tag
  // set is re-anchored in the SAME request that stores the content.
  variables?: SubmitDraftVariables;
};

export function useSubmitL2Draft(
  tenantId: string
): UseMutationResult<SubmitDraftResponse, Error, SubmitL2DraftInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<SubmitDraftResponse>(
        `/tenants/${encodeURIComponent(tenantId)}/templates/draft`,
        { method: "POST", body: input }
      ),
    onSuccess: () => {
      // Broad "templates" prefix so BOTH the L2 list AND the admin management
      // list (useAdminTemplates → ["templates","admin",…]) refetch — a freshly
      // imported/authored draft appears without a hard refresh. [S-211 fix]
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

/**
 * S-216 — POST /tenants/:tid/templates/auto-tag. Sends parsed template
 * content to the admin backend, which calls TemplateAutoTagPort and
 * returns a structured suggestion. 503 surfaces as a mutation error.
 */
export function useAutoTagL2Template(
  tenantId: string
): UseMutationResult<
  import("./l1TemplatesQuery").TemplateTagSuggestion,
  Error,
  { content: string }
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: ({ content }) =>
      api<import("./l1TemplatesQuery").TemplateTagSuggestion>(
        `/tenants/${encodeURIComponent(tenantId)}/templates/auto-tag`,
        { method: "POST", body: { content } }
      ),
  });
}

export function useCertifyL2Template(
  tenantId: string
): UseMutationResult<
  L2TemplateRow,
  Error,
  { templateId: string; effectiveFrom: string }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, effectiveFrom }) =>
      api<L2TemplateRow>(
        `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId)}/certify`,
        { method: "POST", body: { effectiveFrom } }
      ),
    onSuccess: () => {
      // Broad "templates" prefix so the admin management list refetches after a
      // certify (state badge updates without a hard refresh). [S-211 fix]
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}
